import {
  ACCOUNT_PAGE_PATH,
  ADDRESS_HOST_KEYS,
  type Address,
  type AddressError,
  CONNECT_PAGE_PATH,
  CONSENT_PAGE_PATH,
  DOCS_PAGE_PATH,
  formatAddress,
  formatOwnerPath,
  LEGAL_PAGE_PATHS,
  type LegalDocumentKind,
  type OwnerPath,
  parseAddress,
  parseOwnerPath,
  ROOT_PATH,
  SEARCH_PAGE_PATH,
  searchPagePath,
  searchTermOfPath,
} from "@skillcdn/core";
import { hasDocsPage } from "./docs/catalog.js";

// A few kinds of page and no nesting, so the router is a function from a URL to a route.
// Paths are the same in every language; the language travels in the query (i18n/languages.ts).

export type MountView =
  | { readonly kind: "overview"; readonly query: string | undefined; readonly path?: string }
  | { readonly kind: "skill"; readonly path: string }
  | {
      readonly kind: "file";
      readonly path: string;
      /** The section the file is opened at, by the character offset a search result names (`at`). */
      readonly section?: number;
    };

/** A character offset as a search result names a section: digits, within what a file can hold. */
const SECTION_OFFSET = /^\d{1,9}$/;

/**
 * The sections of the pages of whoever is signed in, in the order the side menu lists them. A
 * feature that belongs to a person gets a section here, a path under `/account`, and a page.
 */
export const ACCOUNT_SECTIONS = ["overview", "repositories", "apps", "tokens"] as const;
export type AccountSection = (typeof ACCOUNT_SECTIONS)[number];

export type Route =
  | { readonly name: "landing" }
  | { readonly name: "explore" }
  /** A search across the packs (ADR-0075): the results of a term, or the page of the box alone. */
  | { readonly name: "search"; readonly term: string | undefined }
  | { readonly name: "mount"; readonly address: Address; readonly view: MountView }
  /** The page of an account of the git host: one segment short of an address (ADR-0037). */
  | { readonly name: "owner"; readonly owner: OwnerPath }
  /** The pages of whoever is signed in. */
  | { readonly name: "account"; readonly section: AccountSection }
  /** Where a person is asked whether an app may read an address for them. */
  | { readonly name: "consent" }
  /** Where a person approves a command that asked to connect: for one code, or asking for one. */
  | { readonly name: "connect"; readonly code: string | undefined }
  /** A page of the deployment's own: its terms or its privacy policy (ADR-0029). */
  | { readonly name: "legal"; readonly kind: LegalDocumentKind }
  /** The documentation (ADR-0049): its index without a slug, else one of its pages. */
  | { readonly name: "docs"; readonly slug: string | undefined }
  | { readonly name: "bad-address"; readonly error: AddressError }
  /** Every state of every page, for whoever works on the design. Only in development. */
  | { readonly name: "states" }
  /** The picture behind the social-preview images. Only in development. */
  | { readonly name: "og-card" }
  | { readonly name: "not-found" };

export const PATHS = {
  landing: "/",
  explore: "/explore",
  search: SEARCH_PAGE_PATH,
  docs: DOCS_PAGE_PATH,
  terms: LEGAL_PAGE_PATHS.terms,
  privacy: LEGAL_PAGE_PATHS.privacy,
  account: ACCOUNT_PAGE_PATH,
  consent: CONSENT_PAGE_PATH,
  connect: CONNECT_PAGE_PATH,
  states: "/dev/states",
  ogCard: "/dev/og",
} as const;

/**
 * Where addresses live: a prefix per host, `/gh/` for the git host and `/local/` for a directory
 * of the deployment (ADR-0071). What is under either is an address, good or bad, or the page of an
 * account where the host has those.
 */
const ADDRESS_PREFIXES = ADDRESS_HOST_KEYS.map((host) => `/${host}/`);

/** The path of a section of the account pages; the first section is the account page itself. */
export function accountHref(section: AccountSection = "overview"): string {
  return section === "overview" ? PATHS.account : `${PATHS.account}/${section}`;
}

/** The parameter of the tokens section that names the repository a token is about to be made for. */
export const TOKEN_REPOSITORY_PARAM = "repository";

/** Where a token is made for the repository of an address: the tokens section, with it chosen. */
export function tokensHref(address: Address): string {
  const repository = formatAddress({ ...address, ref: undefined, path: ROOT_PATH });
  return `${accountHref("tokens")}?${TOKEN_REPOSITORY_PARAM}=${encodeURIComponent(repository)}`;
}

/** The path of the page of an account. */
export function ownerHref(owner: OwnerPath): string {
  return formatOwnerPath(owner);
}

/** The page of the results of a term, or the page of the box alone for none. */
export function searchHref(term?: string): string {
  return searchPagePath(term ?? "");
}

/** The path of a page of the documentation, or of its index. */
export function docsHref(slug?: string): string {
  return slug === undefined ? PATHS.docs : `${PATHS.docs}/${slug}`;
}

/** The index at the heading of one of its sections: the step before a page in its breadcrumbs. */
export function docsSectionHref(section: string): string {
  return `${PATHS.docs}#${section}`;
}

/** The page where a command's code is approved, or the page that asks for a code. */
export function connectHref(code?: string): string {
  return code === undefined ? PATHS.connect : `${PATHS.connect}/${code}`;
}

export function matchRoute(pathname: string, search: string, development = false): Route {
  if (pathname === PATHS.landing) {
    return { name: "landing" };
  }
  if (pathname === PATHS.explore) {
    return { name: "explore" };
  }
  if (pathname === PATHS.search || pathname.startsWith(`${PATHS.search}/`)) {
    // The term as written: the server has sent every other spelling to the one page of it.
    const term = searchTermOfPath(pathname);
    return term === undefined
      ? { name: "not-found" }
      : { name: "search", term: term === "" ? undefined : term };
  }
  if (pathname === PATHS.docs) {
    return { name: "docs", slug: undefined };
  }
  if (pathname.startsWith(`${PATHS.docs}/`)) {
    const slug = pathname.slice(PATHS.docs.length + 1);
    return hasDocsPage(slug) ? { name: "docs", slug } : { name: "not-found" };
  }
  if (pathname === PATHS.terms) {
    return { name: "legal", kind: "terms" };
  }
  if (pathname === PATHS.privacy) {
    return { name: "legal", kind: "privacy" };
  }
  if (pathname === PATHS.consent) {
    return { name: "consent" };
  }
  if (pathname === PATHS.connect) {
    return { name: "connect", code: undefined };
  }
  if (pathname.startsWith(`${PATHS.connect}/`)) {
    // One segment, as a person typed or pasted it; the page reads it as a code or says it is none.
    const code = pathname.slice(PATHS.connect.length + 1);
    return code.length === 0 || code.includes("/") || code.length > 16
      ? { name: "not-found" }
      : { name: "connect", code };
  }
  if (pathname === PATHS.account || pathname.startsWith(`${PATHS.account}/`)) {
    const wanted =
      pathname === PATHS.account ? "overview" : pathname.slice(PATHS.account.length + 1);
    const section = ACCOUNT_SECTIONS.find(
      (candidate) => candidate === wanted && accountHref(candidate) === pathname,
    );
    return section === undefined ? { name: "not-found" } : { name: "account", section };
  }
  if (ADDRESS_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    const owner = parseOwnerPath(pathname);
    if (owner !== undefined) {
      return { name: "owner", owner };
    }
    const parsed = parseAddress(pathname);
    if (!parsed.ok) {
      return { name: "bad-address", error: parsed.error };
    }
    const params = new URLSearchParams(search);
    const skill = params.get("skill");
    const file = params.get("file");
    const at = params.get("at");
    const query = params.get("q")?.trim();
    const view: MountView =
      skill !== null && skill.length > 0
        ? { kind: "skill", path: skill }
        : file !== null && file.length > 0
          ? {
              kind: "file",
              path: file,
              ...(at !== null && SECTION_OFFSET.test(at) ? { section: Number(at) } : {}),
            }
          : {
              kind: "overview",
              query: query === undefined || query === "" ? undefined : query,
              ...(params.has("path") ? { path: params.get("path") ?? "" } : {}),
            };
    return { name: "mount", address: parsed.value, view };
  }
  if (development && pathname === PATHS.states) {
    return { name: "states" };
  }
  if (development && pathname === PATHS.ogCard) {
    return { name: "og-card" };
  }
  return { name: "not-found" };
}

/** The URL of a view of a mount, without a language. */
export function mountHref(address: Address, view?: MountView): string {
  const path = formatAddress(address);
  const params = new URLSearchParams();
  if (view?.kind === "skill") {
    params.set("skill", view.path);
  } else if (view?.kind === "file") {
    params.set("file", view.path);
    if (view.section !== undefined) params.set("at", String(view.section));
  } else if (view?.kind === "overview") {
    if (view.path !== undefined && view.path !== address.path) params.set("path", view.path);
    if (view.query !== undefined) params.set("q", view.query);
  }
  const query = params.toString();
  return query.length === 0 ? path : `${path}?${query}`;
}

/** A repository page on GitHub, optionally at a ref and a directory: what people tend to paste. */
const GITHUB_PAGE =
  /^https?:\/\/(?:www\.)?github\.com\/([^/\s?#]+)\/([^/\s?#]+?)(?:\.git)?(?:\/tree\/([^/\s?#]+)(?:\/([^\s?#]*?))?)?\/?(?:[?#].*)?$/i;

/**
 * Turns what a person types into an address: `owner/repo@ref/path`, with or without `/gh/` or
 * this service's origin in front, or the URL of a repository page on GitHub.
 */
export function addressFromInput(input: string): ReturnType<typeof parseAddress> {
  const text = input.trim();
  const page = GITHUB_PAGE.exec(text);
  if (page !== null) {
    const [, owner, repo, ref, path] = page;
    const refPart = ref === undefined ? "" : `@${ref}`;
    const pathPart = path === undefined || path === "" ? "" : `/${path}`;
    return parseAddress(`/gh/${owner}/${repo}${refPart}${pathPart}`);
  }
  const bare = text.replace(/^https?:\/\/[^/]+/i, "").replace(/^\/+/, "");
  if (bare === "") {
    // Nothing typed yet: "a repository is missing" says more than "an empty segment".
    return parseAddress("/gh");
  }
  const hosted = ADDRESS_HOST_KEYS.some((host) => bare.startsWith(`${host}/`));
  return parseAddress(`/${hosted ? bare : `gh/${bare}`}`);
}

/** The page of an account, when what a person typed is an account's name and nothing more. */
export function ownerFromInput(input: string): OwnerPath | undefined {
  const text = input
    .trim()
    .replace(/^https?:\/\/(?:www\.)?github\.com\//i, "")
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/+|\/+$/g, "");
  return parseOwnerPath(`/${/^gh\//.test(text) ? text : `gh/${text}`}`);
}
